import axios from 'axios';

/**
 * H4: 全局 axios 拦截器
 *
 * 统一处理请求超时、响应错误、网络异常，以及关键接口的重试逻辑。
 *
 * 功能：
 * - 请求拦截器：设置10s超时
 * - 响应拦截器：status != 200 → Toast提示
 * - 网络错误 → Toast "网络异常，请稍后重试"
 * - 关键接口（免登/审批提交）自动重试1次（500ms延迟）
 */

// 需要自动重试的关键接口路径（仅限幂等的 GET/查状态类，排除写入类 POST）
const CRITICAL_PATHS = [
    '/api/get_user_access_token',
    '/api/get_sign_parameters',
    '/api/v1/feiShu/getUserInfo',
    '/api/v1/feishu/user/unbind'
];

// 默认超时时间 (ms)
const DEFAULT_TIMEOUT = 10000;

// 重试延迟 (ms)
const RETRY_DELAY = 500;

// 最大重试次数
const MAX_RETRIES = 1;

/**
 * 判断请求是否属于关键接口
 */
function isCriticalRequest(config) {
    if (!config || !config.url) return false;
    return CRITICAL_PATHS.some(path => config.url.includes(path));
}

/**
 * Toast 提示（简易实现，无额外依赖）
 */
function showToast(msg) {
    const existingToast = document.querySelector('.axios-toast-message');
    if (existingToast) {
        existingToast.remove();
    }

    const toast = document.createElement('div');
    toast.className = 'axios-toast-message';
    toast.textContent = msg;
    toast.style.cssText = `
        position: fixed;
        top: 20px;
        left: 50%;
        transform: translateX(-50%);
        background: rgba(0, 0, 0, 0.78);
        color: #fff;
        padding: 10px 20px;
        border-radius: 8px;
        font-size: 14px;
        z-index: 9999;
        pointer-events: none;
        animation: toast-in 0.3s ease-out;
        max-width: 80vw;
        text-align: center;
    `;
    document.body.appendChild(toast);

    setTimeout(() => {
        toast.style.opacity = '0';
        toast.style.transition = 'opacity 0.3s ease';
        setTimeout(() => toast.remove(), 300);
    }, 2500);
}

// 注入关键帧动画样式
if (typeof document !== 'undefined') {
    const style = document.createElement('style');
    style.textContent = `
        @keyframes toast-in {
            from { opacity: 0; transform: translateX(-50%) translateY(-10px); }
            to { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
    `;
    document.head.appendChild(style);
}

/**
 * 注册全局 axios 拦截器
 */
export function registerAxiosInterceptors() {
    // 请求拦截器：设置超时
    axios.interceptors.request.use(
        (config) => {
            // 设置默认超时（保留已有超时设置）
            if (!config.timeout) {
                config.timeout = DEFAULT_TIMEOUT;
            }
            return config;
        },
        (error) => {
            return Promise.reject(error);
        }
    );

    // 响应拦截器：统一错误处理 + 关键接口重试
    axios.interceptors.response.use(
        (response) => {
            // HTTP 状态码非 200 → Toast提示
            if (response.status !== 200) {
                const msg = response.data?.message || response.data?.msg || `请求异常 (${response.status})`;
                showToast(msg);
            }
            return response;
        },
        async (error) => {
            const config = error.config;

            // 网络错误处理
            if (!error.response) {
                // 网络不可达 / 超时
                const isTimeout = error.code === 'ECONNABORTED' || error.message?.includes('timeout');
                const msg = isTimeout ? '请求超时，请稍后重试' : '网络异常，请稍后重试';
                showToast(msg);

                // 关键接口重试逻辑
                if (isCriticalRequest(config)) {
                    // 使用 config 上的自定义重试计数
                    config._retryCount = config._retryCount || 0;
                    if (config._retryCount < MAX_RETRIES) {
                        config._retryCount += 1;
                        console.log(`[Axios] 关键接口重试 (${config._retryCount}/${MAX_RETRIES}): ${config.url}`);
                        // 延迟后重试
                        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
                        return axios(config);
                    }
                    console.warn(`[Axios] 关键接口已达最大重试次数: ${config.url}`);
                }

                return Promise.reject(error);
            }

            // HTTP 错误响应处理
            const status = error.response.status;
            const data = error.response.data;
            const msg = data?.message || data?.msg || `服务器异常 (${status})`;

            if (status >= 500) {
                showToast(`服务器异常 (${status})，请稍后重试`);
            } else if (status === 401 || status === 403) {
                showToast('登录已过期，请刷新页面');
            } else if (status >= 400) {
                showToast(msg);
            }

            return Promise.reject(error);
        }
    );

    console.log('[Axios] 全局拦截器已注册（超时10s + 关键接口重试1次）');
}
